import React, { useState, useEffect } from 'react';
import './App.css';

// Screens
import WelcomeScreen from './components/screens/WelcomeScreen';
import CameraScreen from './components/screens/CameraScreen';
import StyleSelectionScreen from './components/screens/StyleSelectionScreen';
import LoadingScreen from './components/screens/LoadingScreen';
import ResultScreen from './components/screens/ResultScreen';
import QRCodeScreen from './components/screens/QRCodeScreen';
import ImageViewerScreen from './components/screens/ImageViewerScreen';

// Services
import { generateStyledImage } from './services/gemini';
import { saveGeneratedImage } from './services/storage';

// Utils
import { SCREENS } from './utils/constants';
import { getImageIdFromUrl, upscaleImage, addHandlerOverlay, compressForUpload } from './utils/imageUtils';

function App() {
  // Check if this is a mobile viewer request (via QR code)
  const imageIdFromUrl = getImageIdFromUrl();
  const isViewerMode = !!imageIdFromUrl;

  // App state
  const [currentScreen, setCurrentScreen] = useState(
    isViewerMode ? SCREENS.IMAGE_VIEWER : SCREENS.WELCOME
  );
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [compressedImage, setCompressedImage] = useState(null); // Compressed version for upload
  const [imageUrl, setImageUrl] = useState('');
  const [error, setError] = useState(null);
  const [loadingType, setLoadingType] = useState('generating'); // 'generating' or 'saving'

  // Reset app to initial state
  const resetApp = () => {
    setCurrentScreen(SCREENS.WELCOME);
    setCapturedPhoto(null);
    setSelectedStyle(null);
    setGeneratedImage(null);
    setCompressedImage(null);
    setImageUrl('');
    setError(null);
  };

  // Navigation handlers
  const handleStartClick = () => {
    setCurrentScreen(SCREENS.CAMERA);
  };

  const handlePhotoCapture = (photo) => {
    setCapturedPhoto(photo);
    setCurrentScreen(SCREENS.STYLE_SELECTION);
  };

  const handleStyleSelect = async (style) => {
    setSelectedStyle(style);
    setLoadingType('generating');
    setCurrentScreen(SCREENS.LOADING);

    try {
      // Adjust prompt based on generation mode
      let finalPrompt = style.prompt;

      if (style.generationMode === 'environment') {
        // Environment Only: Keep ALL people as-is, just change background naturally
        finalPrompt = `Create a high-quality, professional photograph with a new background environment. CRITICAL REQUIREMENTS:
1. PRESERVE ALL PEOPLE: Keep EVERY person visible in the photo exactly as they are - same appearance, pose, clothing, facial features, and body proportions. DO NOT transform, change, or modify any people in any way.
2. COMPLETE BODIES: Show each person COMPLETELY from head to their natural cut-off point in the original photo. DO NOT cut people in half, float them, or crop them unnaturally. If someone's full body is visible, keep their full body. If only upper body is visible, keep upper body complete.
3. NATURAL COMPOSITION: Maintain the exact same framing and composition as the input photo. Keep all people in their original positions and sizes relative to each other.
4. BACKGROUND ONLY: ONLY change the background environment behind the people. The new environment is: ${style.prompt}.
5. SEAMLESS INTEGRATION: Blend the people naturally into the new environment with appropriate lighting, shadows, and perspective. Enhance photo quality with professional lighting and color grading.
6. PHOTOREALISTIC: All people must remain completely photorealistic - this is a photograph with a new background, NOT an artistic rendering.
Result: A professionally shot photograph where all people are preserved exactly as-is and seamlessly composited into the new background environment.`;
      } else if (style.generationMode === 'transformation') {
        // Full Transformation: Transform MAIN subject, remove background people, create stylized environment
        finalPrompt = `FULL ARTISTIC TRANSFORMATION with background replacement. CRITICAL REQUIREMENTS:
1. IDENTIFY MAIN SUBJECT: Detect the primary person in the photo (usually the person in front, closest to camera, or taking a selfie). This is the ONLY person to transform.
2. TRANSFORM MAIN SUBJECT: Completely transform the main subject into this art style: ${style.prompt}. Every part of them (face, hair, skin, clothing, body) must be rendered in this artistic style with appropriate textures, colors, and artistic techniques. They should NO LONGER look photographic - they must be fully illustrated/animated/painted while maintaining facial recognition.
3. REMOVE BACKGROUND PEOPLE: Any other people visible in the background must be COMPLETELY REMOVED. Do not transform background people - delete them entirely from the scene.
4. CREATE STYLIZED ENVIRONMENT: Generate a completely new background environment that matches the artistic style. The environment should complement the art style (${style.prompt}) with appropriate scenery, colors, and artistic rendering.
5. FRAMING: Match the composition and framing of the input photo. If upper body is shown, keep upper body. Maintain similar pose and position.
6. ARTISTIC CONSISTENCY: Everything in the final image (main subject + environment) must be in the same artistic style - no photographic elements should remain.
Result: A complete artistic transformation showing only the main subject rendered in the artistic style, with all background people removed, set against a stylized environment that matches the art style.`;
      }

      console.log('Generating styled image with:', style.name);
      console.log('Generation mode:', style.generationMode);
      console.log('Final prompt:', finalPrompt);

      const styledImage = await generateStyledImage(
        capturedPhoto,
        finalPrompt
      );

      // Upscale generated image to high resolution (2560x3840) for better quality
      console.log('Upscaling generated image to high resolution...');
      const upscaledImage = await upscaleImage(styledImage, 2560, 3840);

      // Apply Jeep Nation Festival handler overlay to bottom of upscaled image
      console.log('Applying handler overlay to generated image...');
      const imageWithHandler = await addHandlerOverlay(upscaledImage);

      // Create compressed version for upload (happens during AI wait time)
      console.log('Creating compressed version for upload...');
      const compressed = await compressForUpload(imageWithHandler, 1920, 0.85);

      setGeneratedImage(imageWithHandler);
      setCompressedImage(compressed); // Store compressed version for later upload
      setCurrentScreen(SCREENS.RESULT);
    } catch (err) {
      console.error('Error generating image:', err);
      setError(err.message);
      // Fallback: show original photo with error message
      setGeneratedImage(capturedPhoto);
      setCurrentScreen(SCREENS.RESULT);
    }
  };

  const handleRetake = () => {
    setCurrentScreen(SCREENS.CAMERA);
    setCapturedPhoto(null);
    setSelectedStyle(null);
    setGeneratedImage(null);
    setCompressedImage(null);
  };

  const handleNext = async () => {
    setError(null); // Clear any previous errors
    setLoadingType('saving');
    setCurrentScreen(SCREENS.LOADING);

    try {
      // Save compressed image to R2 via n8n (compression already done during generation)
      console.log('Saving compressed image to R2...');
      const { imageId, imageUrl } = await saveGeneratedImage(compressedImage, selectedStyle.id);

      // Use the URL returned directly from n8n workflow
      setImageUrl(imageUrl);

      console.log('Image saved with ID:', imageId);
      console.log('Image URL for QR code:', imageUrl);
      setCurrentScreen(SCREENS.QR_CODE);
    } catch (err) {
      console.error('Error saving image:', err);
      // Use the specific error message from storage.js for better user feedback
      setError(err.message || 'Failed to generate QR code. Please try again.');
      // Stay on result screen or show error
      setCurrentScreen(SCREENS.RESULT);
    }
  };

  const handleDone = () => {
    resetApp();
  };

  const handleAutoReset = () => {
    console.log('Auto-reset triggered');
    resetApp();
  };

  // Disable right-click context menu for kiosk mode
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  // Disable keyboard shortcuts for kiosk mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Disable F11, F5, Ctrl+R, etc.
      if (
        e.key === 'F11' ||
        e.key === 'F5' ||
        (e.ctrlKey && e.key === 'r') ||
        (e.ctrlKey && e.key === 'w') ||
        (e.altKey && e.key === 'F4')
      ) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Render appropriate screen
  if (isViewerMode) {
    return <ImageViewerScreen imageId={imageIdFromUrl} />;
  }

  return (
    <div className="App">
      {currentScreen === SCREENS.WELCOME && (
        <WelcomeScreen onStartClick={handleStartClick} />
      )}

      {currentScreen === SCREENS.CAMERA && (
        <CameraScreen
          onPhotoCapture={handlePhotoCapture}
          onBack={resetApp}
          orientation="portrait"
        />
      )}

      {currentScreen === SCREENS.STYLE_SELECTION && (
        <StyleSelectionScreen
          capturedPhoto={capturedPhoto}
          onStyleSelect={handleStyleSelect}
          onBack={() => setCurrentScreen(SCREENS.CAMERA)}
        />
      )}

      {currentScreen === SCREENS.LOADING && <LoadingScreen type={loadingType} />}

      {currentScreen === SCREENS.RESULT && (
        <ResultScreen
          generatedImage={generatedImage}
          styleName={selectedStyle?.name || 'Unknown'}
          onRetake={handleRetake}
          onNext={handleNext}
        />
      )}

      {currentScreen === SCREENS.QR_CODE && (
        <QRCodeScreen
          imageUrl={imageUrl}
          onDone={handleDone}
          onAutoReset={handleAutoReset}
        />
      )}

      {/* Error Display (if any) */}
      {error && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-red-500/90 backdrop-blur-sm px-6 py-3 rounded-full shadow-xl z-50">
          <p className="text-white font-semibold">{error}</p>
        </div>
      )}
    </div>
  );
}

export default App;
